import { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { chatAPI } from '../lib/api';
import { Link } from 'react-router-dom';
import {
  MessageSquare,
  Send,
  Loader2,
  BookOpen,
  Scale,
  AlertCircle,
  RefreshCw,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from '../hooks/useTranslation';

// exampleQuestions are now fetched via t() in the component

export default function ChatPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const { t } = useTranslation();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await chatAPI.send(userMessage.content, sessionId);
      const data = response.data;

      setSessionId(data.session_id);

      const assistantMessage = {
        role: 'assistant',
        content: data.answer,
        citations: data.citations,
        four_scales: data.four_scales,
        applicable_principles: data.applicable_principles,
        next_steps: data.next_steps,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      toast.error(t('chat.error') || 'Failed to get response. Please try again.');

      const errorMessage = {
        role: 'assistant',
        content: t('chat.error') || 'I encountered an error processing your question. Please try again.',
        error: true,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleExampleClick = (question) => {
    setInput(question);
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setSessionId(null);
    setInput('');
  };

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="chat-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-2 text-muted-foreground text-sm mb-2">
              <MessageSquare className="h-4 w-4" />
              <span>{t('chat.askMode')}</span>
            </div>
            <h1 className="text-3xl font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>
              {t('chat.title')}
            </h1>
            <p className="text-muted-foreground mt-1">
              {t('chat.subtitle')}
            </p>
          </div>
          {messages.length > 0 && (
            <Button variant="outline" onClick={handleNewChat} data-testid="new-chat-btn">
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('chat.newChat')}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Chat Area */}
        <div className="lg:col-span-3">
          <Card className="h-[600px] flex flex-col" data-testid="chat-container">
            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              {messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center p-8">
                  <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mb-4">
                    <Sparkles className="h-8 w-8 text-accent" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                    {t('chat.askQuestion')}
                  </h3>
                  <p className="text-muted-foreground text-sm max-w-md mb-6">
                    {t('chat.description')}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-lg">
                    {t('chat.examples', { returnObjects: true }).map((q, i) => (
                      <Button
                        key={i}
                        variant="outline"
                        className="text-left text-sm h-auto py-3 px-4 whitespace-normal break-words min-h-[60px]"
                        onClick={() => handleExampleClick(q)}
                        data-testid={`example-question-${i}`}
                      >
                        <span className="line-clamp-3">{q}</span>
                      </Button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4" data-testid="messages-list">
                  {messages.map((message, index) => (
                    <div
                      key={index}
                      className={`chat-message ${message.role}`}
                      data-testid={`message-${index}`}
                    >
                      {message.role === 'user' ? (
                        <p>{message.content}</p>
                      ) : (
                        <div className="space-y-4">
                          {/* Main Answer */}
                          <div className="whitespace-pre-wrap">{message.content}</div>

                          {/* Citations */}
                          {message.citations?.length > 0 && (
                            <div>
                              <Separator className="my-3" />
                              <h4 className="text-sm font-medium mb-2 flex items-center">
                                <BookOpen className="h-4 w-4 mr-1" />
                                {t('chat.citations')}
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {message.citations.map((citation, i) => (
                                  <Link
                                    key={i}
                                    to={`/article/${citation.article_number}`}
                                    className="citation-badge hover:bg-accent/30 transition-colors"
                                    data-testid={`citation-${citation.article_number}`}
                                  >
                                    {t('chat.articlePrefix')} {citation.article_number}: {citation.title}
                                  </Link>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Applicable Principles */}
                          {message.applicable_principles?.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium mb-2">{t('chat.applicablePrinciples')}</h4>
                              <div className="flex flex-wrap gap-2">
                                {message.applicable_principles.map((principle, i) => (
                                  <Badge key={i} variant="secondary">
                                    {principle}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Next Steps */}
                          {message.next_steps?.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium mb-2">{t('chat.nextSteps')}</h4>
                              <ul className="text-sm text-muted-foreground space-y-1">
                                {message.next_steps.map((step, i) => (
                                  <li key={i}>• {step}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {message.error && (
                            <div className="flex items-center text-destructive text-sm">
                              <AlertCircle className="h-4 w-4 mr-1" />
                              {t('chat.errorProcessing')}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {loading && (
                    <div className="chat-message assistant" data-testid="loading-message">
                      <div className="flex items-center space-x-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>{t('chat.analyzing')}</span>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>

            {/* Input */}
            <div className="chat-input-container">
              <form onSubmit={handleSubmit} className="flex space-x-2">
                <Textarea
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t('chat.placeholder')}
                  className="min-h-[60px] resize-none"
                  disabled={loading}
                  data-testid="chat-input"
                />
                <Button
                  type="submit"
                  disabled={!input.trim() || loading}
                  className="px-4"
                  data-testid="send-button"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </form>
              <p className="text-xs text-muted-foreground mt-2">
                {t('chat.hint')}
              </p>
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* How it works */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('chat.howItWorks.title')}</CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-3">
              <div className="flex items-start space-x-2">
                <Badge variant="outline" className="mt-0.5">1</Badge>
                <p>{t('chat.howItWorks.step1')}</p>
              </div>
              <div className="flex items-start space-x-2">
                <Badge variant="outline" className="mt-0.5">2</Badge>
                <p>{t('chat.howItWorks.step2')}</p>
              </div>
              <div className="flex items-start space-x-2">
                <Badge variant="outline" className="mt-0.5">3</Badge>
                <p>{t('chat.howItWorks.step3')}</p>
              </div>
            </CardContent>
          </Card>

          {/* Four Scales */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <Scale className="h-4 w-4 mr-2" />
                {t('chat.fourScale.title')}
              </CardTitle>
              <CardDescription>{t('chat.fourScale.subtitle')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {[t('chat.fourScale.necessity'), t('chat.fourScale.proportionality'), t('chat.fourScale.libertyImpact'), t('chat.fourScale.reversibility')].map((scale) => (
                <div key={scale} className="scale-item">
                  <div className="scale-label">{scale}</div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Important Note */}
          <Card className="border-amber-500/50 bg-amber-500/5">
            <CardContent className="pt-4">
              <div className="flex items-start space-x-2 text-sm">
                <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5" />
                <div>
                  <p className="font-medium">{t('chat.notLegalAdvice.title')}</p>
                  <p className="text-muted-foreground">
                    {t('chat.notLegalAdvice.description')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
